import citiesData from '../data/cities.json';
import airlinesData from '../data/airlines.json';
import flightsData from '../data/flights.json';

// Get all cities
export const getAllCities = () => {
  return citiesData;
};

// Get cities by country
export const getCitiesByCountry = (country) => {
  return citiesData.filter(city => city.country === country);
};

// Get UK cities only
export const getUKCities = () => {
  return getCitiesByCountry('United Kingdom');
};

// Get all airlines
export const getAllAirlines = () => {
  return airlinesData;
};

// Get airlines by alliance
export const getAirlinesByAlliance = (alliance) => {
  return airlinesData.filter(airline => airline.alliance === alliance);
};

// Get low-cost airlines
export const getLowCostAirlines = () => {
  return airlinesData.filter(airline => airline.isLowCost === true);
};

// Get premium airlines
export const getPremiumAirlines = () => {
  return airlinesData.filter(airline => airline.isLowCost === false);
};

// Get all flights
export const getAllFlights = () => {
  return flightsData;
};

// Search flights by route
export const searchFlightsByRoute = (from, to, date = null) => {
  let filteredFlights = flightsData.filter(flight => 
    flight.departure.airport === from && flight.arrival.airport === to
  );

  if (date) {
    // Filter by date if provided
    const searchDate = new Date(date);
    const dayOfWeek = searchDate.toLocaleDateString('en-US', { weekday: 'long' });
    filteredFlights = filteredFlights.filter(flight => 
      flight.operatingDays.includes(dayOfWeek)
    );
  }

  return filteredFlights;
};

// Search flights with filters
export const searchFlightsWithFilters = (from, to, filters = {}) => {
  let filteredFlights = searchFlightsByRoute(from, to, filters.date);

  // Apply airline filter
  if (filters.airlines && filters.airlines.length > 0) {
    filteredFlights = filteredFlights.filter(flight => 
      filters.airlines.includes(flight.airline)
    );
  }

  // Apply stops filter
  if (filters.stops !== undefined) {
    filteredFlights = filteredFlights.filter(flight => 
      flight.stops === filters.stops
    );
  }

  // Apply price filter
  if (filters.maxPrice) {
    filteredFlights = filteredFlights.filter(flight => 
      flight.price.economy <= filters.maxPrice
    );
  }

  // Apply cabin class filter
  if (filters.cabinClass) {
    filteredFlights = filteredFlights.filter(flight => 
      flight.cabinClass === filters.cabinClass
    );
  }

  // Apply time filter
  if (filters.departureTime) {
    filteredFlights = filteredFlights.filter(flight => {
      const flightTime = new Date(`2000-01-01 ${flight.departure.time}`);
      const filterTime = new Date(`2000-01-01 ${filters.departureTime}`);
      return flightTime >= filterTime;
    });
  }

  return filteredFlights;
};



// Get airline details by code
export const getAirlineByCode = (code) => {
  return airlinesData.find(airline => airline.code === code);
};

// Get city details by airport code
export const getCityByAirportCode = (airportCode) => {
  return citiesData.find(city => city.code === airportCode);
};

// Format price
export const formatPrice = (price, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

// Format duration
export const formatDuration = (duration) => {
  return duration; // Already formatted as "1h 15m"
};

// Format time
export const formatTime = (time) => {
  return time; // Already formatted as "07:30"
};

// Get flight amenities
export const getFlightAmenities = (flight) => {
  return flight.amenities || [];
};

// Check if flight is direct
export const isDirectFlight = (flight) => {
  return flight.stops === 0;
};

// Get layover information
export const getLayoverInfo = (flight) => {
  if (!flight.layover) return null;
  
  return {
    airport: flight.layover.airport,
    duration: flight.layover.duration,
    terminal: flight.layover.terminal
  };
};

// Sort flights by price
export const sortFlightsByPrice = (flights, ascending = true) => {
  return [...flights].sort((a, b) => {
    const priceA = a.price.economy;
    const priceB = b.price.economy;
    return ascending ? priceA - priceB : priceB - priceA;
  });
};

// Sort flights by duration
export const sortFlightsByDuration = (flights, ascending = true) => {
  return [...flights].sort((a, b) => {
    const durationA = parseDuration(a.duration);
    const durationB = parseDuration(b.duration);
    return ascending ? durationA - durationB : durationB - durationA;
  });
};

// Sort flights by departure time
export const sortFlightsByDepartureTime = (flights, ascending = true) => {
  return [...flights].sort((a, b) => {
    const timeA = new Date(`2000-01-01 ${a.departure.time}`);
    const timeB = new Date(`2000-01-01 ${b.departure.time}`);
    return ascending ? timeA - timeB : timeB - timeA;
  });
};

// Helper function to parse duration string to minutes
const parseDuration = (duration) => {
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    return hours * 60 + minutes;
  }
  return 0;
};

// Get popular routes
export const getPopularRoutes = () => {
  return [
    { from: 'LHR', to: 'MAN', name: 'London to Manchester' },
    { from: 'LHR', to: 'EDI', name: 'London to Edinburgh' },
    { from: 'LHR', to: 'GLA', name: 'London to Glasgow' },
    { from: 'LHR', to: 'BHX', name: 'London to Birmingham' },
    { from: 'MAN', to: 'EDI', name: 'Manchester to Edinburgh' },
    { from: 'MAN', to: 'GLA', name: 'Manchester to Glasgow' },
    { from: 'MAN', to: 'BHX', name: 'Manchester to Birmingham' },
    { from: 'EDI', to: 'GLA', name: 'Edinburgh to Glasgow' },
    { from: 'BHX', to: 'EDI', name: 'Birmingham to Edinburgh' },
    { from: 'BHX', to: 'GLA', name: 'Birmingham to Glasgow' }
  ];
};

// Get flight statistics
export const getFlightStats = (flights) => {
  if (!flights || flights.length === 0) {
    return {
      totalFlights: 0,
      directFlights: 0,
      averagePrice: 0,
      averageDuration: 0,
      airlines: []
    };
  }

  const directFlights = flights.filter(flight => flight.stops === 0).length;
  const totalPrice = flights.reduce((sum, flight) => sum + flight.price.economy, 0);
  const averagePrice = totalPrice / flights.length;
  const airlines = [...new Set(flights.map(flight => flight.airline))];

  return {
    totalFlights: flights.length,
    directFlights,
    averagePrice: Math.round(averagePrice),
    averageDuration: '1h 30m', // Simplified for demo
    airlines
  };
};

// Generate search ID
export const generateSearchId = (from, to, date) => {
  return `${from}-${to}-${date}`;
};

// Validate search parameters
export const validateSearchParams = (from, to, date) => {
  const errors = [];

  if (!from) errors.push('Departure city is required');
  if (!to) errors.push('Arrival city is required');
  if (!date) errors.push('Date is required');

  if (from === to) {
    errors.push('Departure and arrival cities must be different');
  }

  const searchDate = new Date(date);
  const today = new Date();
  if (searchDate < today) {
    errors.push('Date cannot be in the past');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}; 

// Utility functions for flight data processing and bag price calculations

/**
 * Calculate bag prices for a given flight result
 * @param {Object} flightResult - The flight result object
 * @param {number} carryOnCount - Number of carry-on bags
 * @param {number} checkedBagCount - Number of checked bags
 * @returns {Object} Object containing base price and total price with bags
 */
export const calculateBagPrices = (flightResult, carryOnCount = 0, checkedBagCount = 0) => {
  if (!flightResult || !flightResult.price) {
    return { basePrice: 0, totalPrice: 0, bagCosts: {} };
  }

  const basePrice = flightResult.price.economy || flightResult.price.premiumEconomy || flightResult.price.business || 0;
  
  // Bag pricing logic (these could be made configurable per airline)
  const carryOnPrice = 25; // $25 per additional carry-on bag
  const checkedBagPrice = 35; // $35 per checked bag
  
  // Calculate additional bag costs
  const includedCarryOn = flightResult.baggage?.carryOn || 0;
  const includedChecked = flightResult.baggage?.checked || 0;
  
  const additionalCarryOn = Math.max(0, carryOnCount - includedCarryOn);
  const additionalChecked = Math.max(0, checkedBagCount - includedChecked);
  
  const carryOnCost = additionalCarryOn * carryOnPrice;
  const checkedBagCost = additionalChecked * checkedBagPrice;
  const totalBagCost = carryOnCost + checkedBagCost;
  
  return {
    basePrice,
    totalPrice: basePrice + totalBagCost,
    bagCosts: {
      carryOn: carryOnCost,
      checked: checkedBagCost,
      total: totalBagCost
    },
    includedBags: {
      carryOn: includedCarryOn,
      checked: includedChecked
    }
  };
};

/**
 * Calculate bag prices for all flight results
 * @param {Array} flightResults - Array of flight result objects
 * @param {number} carryOnCount - Number of carry-on bags
 * @param {number} checkedBagCount - Number of checked bags
 * @returns {Array} Array of flight results with calculated prices
 */
export const calculateAllBagPrices = (flightResults, carryOnCount = 0, checkedBagCount = 0) => {
  if (!Array.isArray(flightResults)) {
    return [];
  }

  return flightResults.map(result => {
    const priceCalculation = calculateBagPrices(result, carryOnCount, checkedBagCount);
    return {
      ...result,
      priceCalculation
    };
  });
};

/**
 * Get the minimum and maximum prices from flight results
 * @param {Array} flightResults - Array of flight result objects with price calculations
 * @returns {Object} Object containing min and max prices
 */
export const getPriceRange = (flightResults) => {
  if (!Array.isArray(flightResults) || flightResults.length === 0) {
    return { min: 0, max: 0 };
  }

  const prices = flightResults.map(result => {
    if (result.priceCalculation) {
      return result.priceCalculation.totalPrice;
    }
    return result.price?.economy || 0;
  });

  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
};

/**
 * Extract available layover airports from flight data
 */
export const extractLayoverAirports = (flightData) => {
  const layoverAirports = new Set();
  
  flightData.forEach(flight => {
    if (flight.layover && flight.layover.airport) {
      layoverAirports.add(flight.layover.airport);
    }
  });
  
  return Array.from(layoverAirports).sort();
};

/**
 * Get layover airport info with city name
 */
export const getLayoverAirportInfo = (airportCode) => {
  // Map of airport codes to city names
  const airportCityMap = {
    'AUS': 'Austin',
    'BWI': 'Baltimore', 
    'CLT': 'Charlotte',
    'MDW': 'Chicago',
    'ORD': 'Chicago',
    'DAL': 'Dallas',
    'DFW': 'Dallas',
    'DEN': 'Denver',
    'HOU': 'Houston',
    'IAH': 'Houston',
    'LAS': 'Las Vegas',
    'LAX': 'Los Angeles',
    'MSP': 'Minneapolis',
    'BNA': 'Nashville',
    'EWR': 'Newark',
    'MCO': 'Orlando',
    'DTW': 'Detroit',
    'FLL': 'Fort Lauderdale',
    'MIA': 'Miami',
    'JFK': 'New York',
    'LHR': 'London',
    'LGW': 'London',
    'STN': 'London',
    'LCY': 'London',
    'MAN': 'Manchester',
    'BHX': 'Birmingham',
    'EDI': 'Edinburgh',
    'GLA': 'Glasgow'
  };
  
  return {
    code: airportCode,
    city: airportCityMap[airportCode] || airportCode,
    displayName: `${airportCityMap[airportCode] || airportCode} (${airportCode})`
  };
};

 